import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    Tooltip,
    ReferenceLine,
    YAxis,
} from 'recharts';
import { VerticalFlex, AccentLabel, PadContainer } from 'smartsuite-ui';
import {
    timeSeriesXAxis,
    colors,
    lineGraphDotSize,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import SelectorContainer from '../../SelectorContainer';
import useLoadNextPaint from '../../../../shared/hooks/useLoadNextPaint';

// eslint-disable-next-line complexity
function Sparkline(props) {
    const {
        colorPalette = colors,
        lineChartProperties,
        lineProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        hideAverage,
        hideLatest,
        suffix = '',
        timeFormat,
        tooltipProperties,
    } = props.chartProperties;

    const isReadyToPaint = useLoadNextPaint();

    return (
        <SelectorContainer
            isActive={props.isActive}
            isSelectable={props.isSelectable}
            onSelectorClick={props.onSelectorClick}
            onSelectorRemoveClick={props.onSelectorRemoveClick}
            title={props.title}
            titleWidth={props.titleWidth}
        >
            <div className="sparkline__chart">
                {isReadyToPaint && !hideAverage && (
                    <PadContainer cssClassModifiers={['no-pad-top-bottom']}>
                        <VerticalFlex>
                            <AccentLabel>
                                {props.dashboardLabels.average}
                            </AccentLabel>
                            {props.average}
                        </VerticalFlex>
                    </PadContainer>
                )}
                {isReadyToPaint && props.datasetStats.target && (
                    <PadContainer cssClassModifiers={['no-pad-top-bottom']}>
                        <VerticalFlex>
                            <AccentLabel>
                                {props.dashboardLabels.target}
                            </AccentLabel>
                            {props.datasetStats.target}
                        </VerticalFlex>
                    </PadContainer>
                )}
                {isReadyToPaint && (
                    <ResponsiveContainer {...responsiveContainerProperties}>
                        <LineChart
                            {...lineChartProperties}
                            data={props.dataPoints}
                        >
                            <YAxis hide={true} domain={props.domain} />
                            <Tooltip
                                {...tooltipProperties}
                                cursor={
                                    props.dataPoints.length !== 0
                                }
                                content={
                                    <CustomTooltip
                                        activeIndex={0}
                                        axisKey={timeSeriesXAxis}
                                        axisFormatter={props.dateFormatter}
                                        dataFormat={timeFormat}
                                        dataFormatter={props.dataFormatter}
                                        suffix={suffix}
                                        thresholds={
                                            props.threshold
                                                ? [props.threshold]
                                                : []
                                        }
                                    />
                                }
                            />
                            <Line
                                {...lineProperties}
                                dataKey={props.dataKey}
                                stroke={colorPalette[0]}
                                activeDot={{
                                    r: lineGraphDotSize,
                                }}
                            />
                            {props.threshold && (
                                <ReferenceLine
                                    key={`threshold-${props.threshold.id}-${props.threshold.itemId}`}
                                    {...referenceLineProperties}
                                    y={props.threshold.value}
                                    stroke={props.threshold.lineColor}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
            {isReadyToPaint && !hideLatest && (
                <PadContainer cssClassModifiers={['no-pad-top-bottom']}>
                    <VerticalFlex>
                        <AccentLabel>
                            {props.dashboardLabels.latest}
                        </AccentLabel>
                        {props.datasetStats.latest}
                    </VerticalFlex>
                </PadContainer>
            )}
        </SelectorContainer>
    );
}

Sparkline.propTypes = {
    average: PropTypes.string,
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    dataKey: PropTypes.string.isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    dataFormatter: PropTypes.func,
    datasetStats: PropTypes.instanceOf(Object).isRequired,
    domain: PropTypes.instanceOf(Array).isRequired,
    isActive: PropTypes.bool.isRequired,
    isSelectable: PropTypes.bool.isRequired,
    onSelectorClick: PropTypes.func.isRequired,
    onSelectorRemoveClick: PropTypes.func.isRequired,
    threshold: PropTypes.instanceOf(Object),
    title: PropTypes.string.isRequired,
    titleWidth: PropTypes.number,
};

Sparkline.defaultProps = {
    average: undefined,
    dataFormatter: undefined,
    dataPoints: [],
    threshold: undefined,
};

/* eslint-disable complexity */
export default React.memo(
    Sparkline,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.threshold, nextProps.threshold) &&
        _.isEqual(prevProps.isActive, nextProps.isActive) &&
        _.isEqual(prevProps.titleWidth, nextProps.titleWidth)
);
